import { z } from 'zod';

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Enter a valid email address.'));

export const customerEmailSchema = emailSchema;
export type CustomerEmail = z.infer<typeof customerEmailSchema>;

export const passwordRequirements = [
  { id: 'length', label: 'At least 10 characters', test: (value: string) => value.length >= 10 },
  { id: 'uppercase', label: 'One uppercase letter', test: (value: string) => /[A-Z]/.test(value) },
  { id: 'lowercase', label: 'One lowercase letter', test: (value: string) => /[a-z]/.test(value) },
  { id: 'number', label: 'One number', test: (value: string) => /\d/.test(value) },
  {
    id: 'special',
    label: 'One special character',
    test: (value: string) => /[^A-Za-z0-9]/.test(value),
  },
] as const;

export const passwordSchema = z.string().superRefine((value, context) => {
  for (const requirement of passwordRequirements) {
    if (!requirement.test(value)) {
      context.addIssue({ code: 'custom', message: requirement.label });
    }
  }
});

export const verificationCodeSchema = z.string().regex(/^\d{6}$/, 'Enter the six-digit code.');

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password.'),
  rememberMe: z.boolean().default(false),
});

export const registrationSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Enter your first name.'),
    lastName: z.string().trim().min(1, 'Enter your last name.'),
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    acceptTerms: z.boolean().refine(Boolean, 'Accept the Terms and Privacy Policy.'),
    marketingOptIn: z.boolean().default(false),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match.',
  });

export const emailVerificationSchema = z.object({
  email: emailSchema,
  code: verificationCodeSchema,
});

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z
  .object({
    email: emailSchema,
    code: verificationCodeSchema,
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match.',
  });

export const authSessionSchema = z.object({
  user: z.object({
    id: z.string().min(1),
    email: emailSchema,
    emailVerified: z.boolean(),
    firstName: z.string(),
    lastName: z.string(),
    displayName: z.string(),
    roles: z.array(z.enum(['customer', 'admin', 'workshop'])),
    createdAt: z.iso.datetime().optional(),
  }),
  expiresAt: z.iso.datetime(),
});

export type LoginFormInput = z.input<typeof loginSchema>;
export type LoginFormValues = z.output<typeof loginSchema>;
export type RegistrationFormInput = z.input<typeof registrationSchema>;
export type RegistrationFormValues = z.output<typeof registrationSchema>;
export type EmailVerificationFormValues = z.output<typeof emailVerificationSchema>;
export type ForgotPasswordFormValues = z.output<typeof forgotPasswordSchema>;
export type ResetPasswordFormValues = z.output<typeof resetPasswordSchema>;

export const supportedLocaleSchema = z.enum(['en-US', 'tr-TR']);

export const customerNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a name.')
  .max(80, 'Use 80 characters or fewer.')
  .refine(
    (value) => Array.from(value).every((character) => character.charCodeAt(0) >= 32),
    'Control characters are not allowed.',
  )
  .refine(
    (value) => /^[\p{L}\p{M} '\u2019-]+$/u.test(value),
    'Use letters, spaces, apostrophes, or hyphens.',
  )
  .refine((value) => /\p{L}/u.test(value), 'Enter at least one letter.');

export const expectedVersionSchema = z.int().positive();
export const policyVersionSchema = z.string().trim().min(1).max(64);

export const communicationPreferencesSchema = z.object({
  transactionalEmail: z.literal(true),
  productUpdatesEmail: z.boolean(),
  promotionsEmail: z.boolean(),
});

export const customerProfileSchema = z.object({
  userId: z.string().min(1),
  email: emailSchema,
  emailVerified: z.boolean(),
  firstName: customerNameSchema,
  lastName: customerNameSchema,
  displayName: z.string().min(1),
  locale: supportedLocaleSchema,
  communicationPreferences: communicationPreferencesSchema,
  profileStatus: z.literal('active'),
  version: expectedVersionSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const customerProfileResultSchema = z.object({
  profile: customerProfileSchema,
  consentSetupRequired: z.boolean(),
});

export const profileUpdateSchema = z.object({
  firstName: customerNameSchema,
  lastName: customerNameSchema,
  locale: supportedLocaleSchema,
  expectedVersion: expectedVersionSchema,
});

export const communicationPreferencesUpdateSchema = z.object({
  productUpdatesEmail: z.boolean(),
  promotionsEmail: z.boolean(),
  expectedVersion: expectedVersionSchema,
  policyVersion: policyVersionSchema,
});

export const emailChangeRequestSchema = z.object({ newEmail: emailSchema });
export const emailChangeConfirmationSchema = z.object({
  newEmail: emailSchema,
  code: verificationCodeSchema,
});

export const consentInitializationSchema = z
  .object({
    termsPolicyVersion: policyVersionSchema,
    privacyPolicyVersion: policyVersionSchema,
    marketingEmail: z.boolean().optional(),
    marketingPolicyVersion: policyVersionSchema.optional(),
  })
  .refine((value) => value.marketingEmail === undefined || Boolean(value.marketingPolicyVersion), {
    path: ['marketingPolicyVersion'],
    message: 'A marketing policy version is required with a marketing choice.',
  });

export const consentAuditRecordSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  consentType: z.enum(['terms', 'privacy', 'marketing-email', 'product-updates-email']),
  action: z.enum(['granted', 'withdrawn', 'acknowledged']),
  policyVersion: policyVersionSchema,
  occurredAt: z.iso.datetime(),
  source: z.enum(['registration', 'account-settings', 'system-migration']),
  requestId: z.string().optional(),
});

export const consentHistoryPageSchema = z.object({
  items: z.array(consentAuditRecordSchema),
  nextCursor: z.string().nullable(),
});

export type ProfileUpdateFormValues = z.output<typeof profileUpdateSchema>;
export type CommunicationPreferencesFormValues = z.output<
  typeof communicationPreferencesUpdateSchema
>;
export type EmailChangeRequestFormValues = z.output<typeof emailChangeRequestSchema>;
export type EmailChangeConfirmationFormValues = z.output<typeof emailChangeConfirmationSchema>;

const noHtml = (value: string) => !/<\/?[a-z][^>]*>/i.test(value);
const plainText = (maximum: number, minimum = 1) =>
  z
    .string()
    .trim()
    .min(minimum)
    .max(maximum)
    .refine((value) => !/[\p{Cc}\p{Cf}]/u.test(value), 'Control characters are not allowed.')
    .refine(noHtml, 'HTML is not allowed.');
export const reservedCatalogSlugs = new Set([
  'admin',
  'api',
  'account',
  'login',
  'register',
  'search',
  'products',
  'checkout',
  'orders',
  'workshop',
  'not-found',
]);
export function normalizeCatalogSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
export const catalogSlugSchema = z
  .string()
  .transform(normalizeCatalogSlug)
  .pipe(
    z
      .string()
      .min(1, 'Provide a valid ASCII slug.')
      .max(100)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .refine((slug) => !reservedCatalogSlugs.has(slug), 'This slug is reserved.'),
  );
export const skuSchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(
    z
      .string()
      .min(2)
      .max(64)
      .regex(/^[A-Z0-9_-]+$/, 'Use letters, numbers, hyphens, or underscores.'),
  );
export const moneySchema = z.object({
  amountMinor: z.int().safe().nonnegative(),
  currency: z.literal('USD'),
});
export const seoMetadataSchema = z.object({
  title: plainText(70).optional(),
  description: plainText(180).optional(),
});
export const productMediaSchema = z.object({
  id: z.string().trim().min(1).max(80).optional(),
  kind: z.literal('image'),
  role: z.enum(['primary', 'gallery', 'thumbnail', 'customization-preview']),
  objectKey: z
    .string()
    .trim()
    .min(1)
    .max(512)
    .regex(/^catalog\/(?:products|categories|placeholders)\/[A-Za-z0-9][A-Za-z0-9._/-]*$/)
    .refine(
      (key) => !key.includes('..') && !key.includes('\\') && !key.includes('://'),
      'Invalid media object key.',
    ),
  altText: plainText(200),
  width: z.int().positive().max(20000).optional(),
  height: z.int().positive().max(20000).optional(),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']).optional(),
  displayOrder: z.int().nonnegative().max(1000),
});
const optionValueSchema = z.object({
  id: z.string().min(1).max(80).optional(),
  value: plainText(100),
  label: plainText(100),
  displayOrder: z.int().nonnegative().max(1000),
  priceAdjustment: moneySchema.optional(),
  active: z.boolean(),
});
export const productOptionSchema = z
  .object({
    id: z.string().min(1).max(80).optional(),
    name: plainText(80),
    label: plainText(100),
    required: z.boolean(),
    displayOrder: z.int().nonnegative().max(1000),
    values: z.array(optionValueSchema).min(1).max(50),
  })
  .superRefine((option, context) => {
    const ids = option.values.map((value) => value.id).filter(Boolean);
    if (new Set(ids).size !== ids.length)
      context.addIssue({
        code: 'custom',
        path: ['values'],
        message: 'Option value IDs must be unique.',
      });
    if (option.required && !option.values.some((value) => value.active))
      context.addIssue({
        code: 'custom',
        path: ['values'],
        message: 'Required options need an active value.',
      });
  });
export const productVariantSchema = z.object({
  id: z.string().min(1).max(80).optional(),
  sku: skuSchema,
  optionValueIds: z.array(z.string().min(1)).max(10),
  priceOverride: moneySchema.optional(),
  active: z.boolean(),
});
const customizationTextSchema = z.object({
  id: z.string().min(1).max(80).optional(),
  label: plainText(100),
  required: z.boolean(),
  maxLength: z.int().positive().max(500),
  multiline: z.boolean(),
  placeholder: plainText(160).optional(),
});
export const customizationSchema = z.object({
  mode: z.enum(['none', 'wood-engraving', 'ornament', 'tshirt-print']),
  textInputs: z.array(customizationTextSchema).max(10),
  imageUpload: z
    .object({
      required: z.boolean(),
      acceptedContentTypes: z
        .array(z.enum(['image/jpeg', 'image/png', 'image/webp']))
        .min(1)
        .max(3),
      maxFiles: z.int().positive().max(5),
      maxFileSizeBytes: z
        .int()
        .positive()
        .max(25 * 1024 * 1024),
    })
    .optional(),
  designArea: z
    .object({
      width: z.number().positive().max(10000),
      height: z.number().positive().max(10000),
      unit: z.literal('px'),
      safeAreaInset: z.number().nonnegative().max(1000).optional(),
    })
    .optional(),
  customerNotesAllowed: z.boolean(),
});
const categoryFields = {
  name: plainText(120),
  slug: catalogSlugSchema.optional(),
  shortDescription: plainText(240),
  description: plainText(5000),
  displayOrder: z.int().nonnegative().max(100000),
  heroMedia: productMediaSchema.optional(),
  seo: seoMetadataSchema,
};
export const createCategorySchema = z.object(categoryFields);
export const updateCategorySchema = z.object({
  ...categoryFields,
  slug: catalogSlugSchema,
  expectedVersion: expectedVersionSchema,
});
export const catalogLifecycleSchema = z.object({ expectedVersion: expectedVersionSchema });
export const catalogStatusSchema = z.enum(['draft', 'published', 'archived']);
const productFields = {
  name: plainText(160),
  slug: catalogSlugSchema.optional(),
  sku: skuSchema,
  categoryId: z.string().min(1).max(100),
  productType: z.enum(['wood-engraving', 'ornament', 'tshirt']),
  shortDescription: plainText(300),
  description: plainText(10000),
  basePrice: moneySchema,
  compareAtPrice: moneySchema.optional(),
  badges: z.array(z.enum(['bestseller', 'new', 'popular', 'seasonal'])).max(4),
  featured: z.boolean(),
  featuredOrder: z.int().nonnegative().max(100000).optional(),
  merchandisingOrder: z.int().nonnegative().max(100000),
  customizable: z.boolean(),
  customization: customizationSchema,
  options: z.array(productOptionSchema).max(10),
  variants: z.array(productVariantSchema).max(500),
  media: z.array(productMediaSchema).max(20),
  seo: seoMetadataSchema,
};
function validateProductStructure(
  product: z.infer<z.ZodObject<typeof productFields>>,
  context: z.RefinementCtx,
) {
  if (product.compareAtPrice && product.compareAtPrice.amountMinor <= product.basePrice.amountMinor)
    context.addIssue({
      code: 'custom',
      path: ['compareAtPrice'],
      message: 'Compare-at price must exceed base price.',
    });
  if (product.featured && product.featuredOrder === undefined)
    context.addIssue({
      code: 'custom',
      path: ['featuredOrder'],
      message: 'Featured order is required.',
    });
  const compatible = {
    'wood-engraving': ['none', 'wood-engraving'],
    ornament: ['none', 'ornament'],
    tshirt: ['none', 'tshirt-print'],
  } as const;
  if (
    !compatible[product.productType].includes(product.customization.mode as never) ||
    (!product.customizable && product.customization.mode !== 'none')
  )
    context.addIssue({
      code: 'custom',
      path: ['customization', 'mode'],
      message: 'Customization mode is incompatible with this product.',
    });
  const optionIds = product.options.map((option) => option.id).filter(Boolean);
  if (new Set(optionIds).size !== optionIds.length)
    context.addIssue({ code: 'custom', path: ['options'], message: 'Option IDs must be unique.' });
  const mediaIds = product.media.map((media) => media.id).filter(Boolean);
  if (new Set(mediaIds).size !== mediaIds.length)
    context.addIssue({ code: 'custom', path: ['media'], message: 'Media IDs must be unique.' });
  const variantIds = product.variants.map((variant) => variant.id).filter(Boolean);
  const variantSkus = product.variants.map((variant) => variant.sku);
  const combinations = product.variants.map((variant) =>
    [...variant.optionValueIds].sort().join('|'),
  );
  if (new Set(variantIds).size !== variantIds.length)
    context.addIssue({
      code: 'custom',
      path: ['variants'],
      message: 'Variant IDs must be unique.',
    });
  if (new Set(variantSkus).size !== variantSkus.length)
    context.addIssue({
      code: 'custom',
      path: ['variants'],
      message: 'Variant SKUs must be unique.',
    });
  if (new Set(combinations).size !== combinations.length)
    context.addIssue({
      code: 'custom',
      path: ['variants'],
      message: 'Variant combinations must be unique.',
    });
  const values = new Map(
    product.options.flatMap((option) =>
      option.values.map((value) => [value.id, option.id] as const),
    ),
  );
  for (const [index, variant] of product.variants.entries()) {
    const owners = variant.optionValueIds.map((id) => values.get(id));
    if (owners.some((owner) => !owner) || new Set(owners).size !== owners.length)
      context.addIssue({
        code: 'custom',
        path: ['variants', index, 'optionValueIds'],
        message: 'Variant option values are invalid.',
      });
  }
}
export const createProductSchema = z.object(productFields).superRefine(validateProductStructure);
export const updateProductSchema = z
  .object({ ...productFields, slug: catalogSlugSchema, expectedVersion: expectedVersionSchema })
  .superRefine(validateProductStructure);
export const duplicateProductSchema = z.object({
  name: plainText(160).optional(),
  slug: catalogSlugSchema.optional(),
  sku: skuSchema,
});
export const catalogPaginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().max(2000).optional(),
});
export const catalogMediaContentTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']);
export const catalogMediaStatusSchema = z.enum([
  'initiated',
  'uploaded',
  'processing',
  'ready',
  'failed',
  'expired',
  'deleted',
]);
export const catalogMediaOwnerTypeSchema = z.enum(['unassigned', 'product', 'category']);
export const catalogMediaFailureCodeSchema = z.enum([
  'unsupported-format',
  'content-type-mismatch',
  'file-too-large',
  'dimensions-too-small',
  'dimensions-too-large',
  'pixel-count-too-large',
  'corrupt-image',
  'animated-image-not-supported',
  'processing-timeout',
  'source-object-missing',
  'storage-error',
  'internal-processing-error',
]);
export const catalogMediaVariantSchema = z.object({
  name: z.enum(['thumbnail', 'card', 'detail', 'zoom', 'original-display']),
  objectKey: z.string().regex(/^catalog\/[a-zA-Z0-9-]+\/v\d+\/[a-z-]+\.webp$/),
  width: z.number().int().positive().max(12000),
  height: z.number().int().positive().max(12000),
  contentType: z.literal('image/webp'),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(20 * 1024 * 1024),
  etag: z.string().min(1).max(200).optional(),
});
export const initiateCatalogMediaUploadSchema = z.object({
  fileName: z
    .string()
    .trim()
    .min(1)
    .max(180)
    .refine(
      (value) =>
        ![...value].some((character) => {
          const codePoint = character.codePointAt(0) ?? 0;
          return codePoint <= 31 || codePoint === 127 || character === '/' || character === '\\';
        }),
      'File name is unsafe.',
    ),
  contentType: catalogMediaContentTypeSchema,
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(20 * 1024 * 1024),
});
export const adminCatalogMediaListSchema = z
  .object({
    status: catalogMediaStatusSchema.default('ready'),
    ownerType: catalogMediaOwnerTypeSchema.optional(),
    ownerId: z.string().trim().min(1).max(120).optional(),
    exactMediaId: z.uuid().optional(),
    sort: z.enum(['newest', 'oldest']).default('newest'),
    limit: z.coerce.number().int().min(1).max(100).default(24),
    cursor: z.string().min(1).max(2000).optional(),
  })
  .refine(
    (input) => !input.ownerId || input.ownerType === 'product' || input.ownerType === 'category',
    {
      path: ['ownerId'],
      message: 'Owner ID requires a product or category owner type.',
    },
  );
export const attachCatalogMediaSchema = z.object({
  ownerType: z.enum(['product', 'category']),
  ownerId: z.string().trim().min(1).max(120),
  role: z.enum(['primary', 'gallery', 'thumbnail', 'customization-preview']),
  altText: plainText(240),
  displayOrder: z.number().int().nonnegative().max(100000),
  expectedOwnerVersion: expectedVersionSchema,
});
export const detachCatalogMediaSchema = z.object({ expectedOwnerVersion: expectedVersionSchema });
export type CreateCategoryValues = z.output<typeof createCategorySchema>;
export type UpdateCategoryValues = z.output<typeof updateCategorySchema>;
export type CreateProductValues = z.output<typeof createProductSchema>;
export type UpdateProductValues = z.output<typeof updateProductSchema>;
export type InitiateCatalogMediaUploadValues = z.output<typeof initiateCatalogMediaUploadSchema>;
